import { FC } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LeadWithFunnel } from '../../types';
import { HiOutlineMail, HiOutlineCalendar, HiOutlinePhone } from 'react-icons/hi';

export interface LeadCardProps {
  lead: LeadWithFunnel;
  onClick?: (lead: LeadWithFunnel) => void;
  variant?: 'default' | 'compact';
  draggable?: boolean;
  accentColor?: string;
  campaignLabel?: string;
  showCampaign?: boolean;
}

export const LeadCard: FC<LeadCardProps> = ({
  lead,
  onClick,
  variant = 'default',
  draggable = false,
  accentColor,
  campaignLabel,
  showCampaign = false,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentBorder = useColorModeValue('blue.500', 'blue.300');
  const subtleText = useColorModeValue('gray.500', 'gray.400');
  const draggingBg = useColorModeValue('blue.50', 'blue.900');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: lead.id.toString(),
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dealValueNumber = lead.deal_value ? Number.parseFloat(lead.deal_value) : null;
  const formattedDealValue =
    dealValueNumber !== null && !Number.isNaN(dealValueNumber)
      ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(dealValueNumber)
      : undefined;
  const expectedClose = lead.expected_close_at
    ? new Date(lead.expected_close_at)
    : null;
  const expectedCloseBadge = expectedClose
    ? expectedClose.toLocaleDateString()
    : undefined;

  const CampaignBadge = () =>
    showCampaign && lead.campaign_link_id ? (
      <Badge
        colorScheme="purple"
        variant="subtle"
        borderRadius="full"
        px={2.5}
        py={0.5}
        fontSize="xs"
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {campaignLabel || `#${lead.campaign_link_id}`}
      </Badge>
    ) : null;

  const FooterBadges = () => {
    const showCampaignBadge = showCampaign && !!lead.campaign_link_id;
    if (!expectedCloseBadge && !showCampaignBadge) return null;

    return (
      <HStack justify="space-between" pt={1} spacing={2}>
        {expectedCloseBadge && (
          <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2.5} py={0.5}>
            <HStack spacing={1} align="center">
              <Icon as={HiOutlineCalendar} boxSize="0.75em" />
              <Text fontSize="xs">{expectedCloseBadge}</Text>
            </HStack>
          </Badge>
        )}
        <HStack spacing={2}>{showCampaignBadge && <CampaignBadge />}</HStack>
      </HStack>
    );
  };

  const cardContent = (
    <VStack align="stretch" spacing={3} pr={2}>
      <VStack align="start" spacing={1}>
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
          {lead.name}
        </Text>
        <HStack spacing={1} color={subtleText} fontSize="xs">
          <Icon as={HiOutlineMail} />
          <Text noOfLines={1}>{lead.email}</Text>
        </HStack>
        {lead.phone && (
          <HStack spacing={1} color={subtleText} fontSize="xs">
            <Icon as={HiOutlinePhone} />
            <Text noOfLines={1}>{lead.phone}</Text>
          </HStack>
        )}
      </VStack>
      {formattedDealValue && (
        <Text fontSize="sm" fontWeight="semibold">
          {formattedDealValue}
        </Text>
      )}
      <FooterBadges />
    </VStack>
  );

  if (variant === 'compact') {
    return (
      <Box
        ref={draggable ? setNodeRef : undefined}
        {...(draggable ? attributes : {})}
        {...(draggable ? listeners : {})}
        style={style}
        p={3}
        bg={isDragging ? draggingBg : cardBg}
        border="1px"
        borderColor={isDragging ? accentBorder : borderColor}
        borderRadius="xl"
        boxShadow={isDragging ? 'md' : 'sm'}
        borderLeft="4px solid"
        borderLeftColor={accentColor || accentBorder}
        overflow="hidden"
        cursor={isDragging ? 'grabbing' : draggable ? 'grab' : onClick ? 'pointer' : 'default'}
        onClick={(e) => {
          // Don't trigger click if we just finished dragging
          if (isDragging) {
            e.stopPropagation();
            return;
          }
          onClick?.(lead);
        }}
        _hover={onClick && !isDragging ? { bg: hoverBg } : {}}
        _active={draggable && !isDragging ? { cursor: 'grabbing' } : {}}
      >
        {cardContent}
      </Box>
    );
  }

  return (
    <Box
      ref={draggable ? setNodeRef : undefined}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      style={style}
      p={4}
      bg={isDragging ? draggingBg : cardBg}
      border="1px"
      borderColor={isDragging ? (accentColor || accentBorder) : borderColor}
      borderRadius="xl"
      borderLeft="5px solid"
      borderLeftColor={accentColor || accentBorder}
      boxShadow={isDragging ? 'xl' : 'base'}
      overflow="hidden"
      cursor={isDragging ? 'grabbing' : draggable ? 'grab' : onClick ? 'pointer' : 'default'}
      onClick={(e) => {
        // Don't trigger click if we just finished dragging
        if (isDragging) {
          e.stopPropagation();
          return;
        }
        onClick?.(lead);
      }}
      _hover={onClick && !isDragging ? { bg: hoverBg } : {}}
      _active={draggable && !isDragging ? { cursor: 'grabbing' } : {}}
    >
      <VStack align="stretch" spacing={2}>
        <VStack align="start" spacing={1}>
          <Text fontSize="md" fontWeight="semibold">
            {lead.name}
          </Text>
          <HStack spacing={2} color={subtleText} fontSize="sm">
            <Icon as={HiOutlineMail} />
            <Text>{lead.email}</Text>
          </HStack>
          {lead.phone && (
            <HStack spacing={2} color={subtleText} fontSize="sm">
              <Icon as={HiOutlinePhone} />
              <Text>{lead.phone}</Text>
            </HStack>
          )}
        </VStack>
        {formattedDealValue && (
          <Text fontSize="sm" fontWeight="semibold">
            {formattedDealValue}
          </Text>
        )}
        <FooterBadges />
      </VStack>
    </Box>
  );
};
